import { EnvelopeIcon } from '@heroicons/react/24/outline'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@/components/Dialog'
import { TextField } from '@/components/TextField'

type EmailSetupDialogProps = {
    open: boolean
    onClose: () => void
    onSubmit: (code: string) => Promise<void>
    onResend: () => Promise<void>
}

export const EmailSetupDialog = ({ open, onClose, onSubmit, onResend }: EmailSetupDialogProps) => {
    const { t } = useTranslation()

    const codeInputRef = useRef<HTMLInputElement | null>(null)
    const [code, setCode] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [localError, setLocalError] = useState<string | null>(null)

    if (!open) {
        return null
    }

    const handleSubmit = async () => {
        setSubmitting(true)
        try {
            await onSubmit(code)
            setCode('')
        } catch (e) {
            setLocalError(e instanceof Error ? e.message : t('messages.emailVerifyError'))
        } finally {
            setSubmitting(false)
        }
    }

    const handleResend = async () => {
        setSubmitting(true)
        try {
            await onResend()
        } catch {
            // parent handles it
        } finally {
            setSubmitting(false)
        }
    }

    const handleClose = () => {
        setCode('')
        setLocalError(null)
        onClose()
    }

    return (
        <Dialog
            open={open}
            title={t('mfa.emailPendingTitle')}
            onClose={handleClose}
            initialFocusRef={codeInputRef}
            closeLabel={t('common.close')}
            footer={
                <div className="flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => {
                            void handleResend()
                        }}
                        disabled={submitting}
                        className="text-account-secondary hover:text-account-strong text-sm font-semibold underline underline-offset-4 transition disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {t('mfa.resendEmail')}
                    </button>
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="border-account-border bg-account-surface text-account-primary hover:bg-account-page rounded-lg border px-4 py-2 font-semibold transition"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                void handleSubmit()
                            }}
                            disabled={submitting || !code}
                            className="bg-account-accent text-account-on-accent hover:bg-account-accent-hover rounded-lg px-6 py-2 font-bold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {t('common.verifyCode')}
                        </button>
                    </div>
                </div>
            }
        >
            <div className="border-account-accent-border bg-account-accent-soft text-account-accent-text flex items-start gap-3 rounded-lg border px-4 py-3 text-sm">
                <EnvelopeIcon className="mt-0.5 h-5 w-5 shrink-0" />
                <p>{t('mfa.emailPendingDescription')}</p>
            </div>

            <TextField
                inputRef={codeInputRef}
                label={t('mfa.emailCodeLabel')}
                value={code}
                placeholder="000000"
                centered
                onChange={(value) => {
                    setCode(value)
                    setLocalError(null)
                }}
                error={localError}
            />
        </Dialog>
    )
}
